import React from 'react';
import { Tooltip } from '@material-tailwind/react';
import { useTranslation } from 'react-i18next';
import { getStatusColorForDay, statusTextColorMap, StatusType } from '../../types/types';
import { Flag16Filled, Flag16Regular } from '@fluentui/react-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import Loading from './Loading';
import type { PodMetric } from '../../store/slices/podMetricsSlice';

interface StatusHistoryTableProps {
  days: number[];
  podsByContainer: Record<string, PodMetric[]>;
  lagByClientId: Record<string, number>;
  metricsLoading: boolean;
  metricsError: string | null;
  selectedYear: number;
  selectedMonth: number;
  currentMonth: number;
  currentYear: number;
  todayDate: number;
  metricsByDayAndPod: Record<string, PodMetric[]>;
  handleHistoricalMetricsClick: (containerName: string, podName?: string, day?: number) => void;
}

const StatusHistoryTable: React.FC<StatusHistoryTableProps> = ({
  days,
  podsByContainer,
  lagByClientId,
  metricsLoading,
  metricsError,
  selectedYear,
  selectedMonth,
  currentMonth,
  currentYear,
  todayDate,
  metricsByDayAndPod,
  handleHistoricalMetricsClick
}) => {
  const { t } = useTranslation();
  
  // Get critical services from Redux store at the component level
  const { criticalServices } = useSelector((state: RootState) => state.criticalServices);
  
  // Create a map of service names to critical status for quick lookup
  const criticalServicesMap: Record<string, boolean> = {};
  
  // Populate the map if criticalServices is available
  if (criticalServices && criticalServices.length > 0) {
    criticalServices.forEach(service => {
      if (service.enabled) {
        criticalServicesMap[service.service_name] = true;
      }
    });
  }
  const isTodayVisible = selectedMonth === currentMonth && selectedYear === currentYear;

  // Check if a day is in the future (to show empty cells)
  const isFutureDay = (day: number) => {
    // If we're looking at a future month
    if (selectedYear > currentYear || (selectedYear === currentYear && selectedMonth > currentMonth)) {
      return true;
    }
    
    // If we're looking at the current month, days after today are future days
    if (selectedYear === currentYear && selectedMonth === currentMonth) {
      return day > todayDate;
    }
    
    return false;
  };

  // Check if we should show data for a specific day
  const isCurrentDay = (day: number) => {
    // If we're looking at a past month, show data for all days
    if (selectedYear < currentYear || (selectedYear === currentYear && selectedMonth < currentMonth)) {
      return true;
    }
    
    // If we're looking at the current month, only show data for days up to today
    if (selectedYear === currentYear && selectedMonth === currentMonth) {
      return day <= todayDate;
    }
    
    // For future months, don't show any data
    return false;
  };

  // Get historical status for a specific day
  const getHistoricalStatusForDay = (containerName: string, day: number) => {
    if (metricsByDayAndPod === undefined || Object.keys(metricsByDayAndPod).length === 0) {
      return { status: 'empty_cell' };
    }
    
    // Use the metrics lookup
    const containerDayKey = `container-${containerName}-${day}`;
    const containerMetrics = metricsByDayAndPod[containerDayKey];
    
    if (!containerMetrics || containerMetrics.length === 0) {
      return { status: 'empty_cell' };
    }

    // add maintenance check here
    const maintenancePod = containerMetrics.find(pod => pod.phase === 'Maintenance');
    if (maintenancePod) {
      return { 
        status: 'maintenance',
        reason: maintenancePod.event_reason || 'Scheduled maintenance',
        createdAt: maintenancePod.created_at || '',
      };
    }
    
    // Check if any pods were in failed or pending state or have error/warning logs
    const hasFailedPods = containerMetrics.some(pod => 
      (pod.phase === 'Failed') &&
      (pod.log_capture?.log_level)
    );
    
    const hasPendingPods = containerMetrics.some(pod => 
      pod.phase === 'Pending' &&
      (pod.log_capture?.log_level)
    );
    
    if (hasFailedPods) {
      return { status: 'outage' };
    } else if (hasPendingPods) {
      return { status: 'warning' };
    } else {
      return { status: 'operational' };
    }
  };

  // Get detailed status counts for a specific day
  const getDayStatusCounts = (containerName: string, day: number) => {
    if (metricsByDayAndPod === undefined || Object.keys(metricsByDayAndPod).length === 0) {
      return { failed: 0, pending: 0, running: 0 };
    }
    
    // Use the metrics lookup
    const containerDayKey = `container-${containerName}-${day}`;
    const containerMetrics = metricsByDayAndPod[containerDayKey] || [];
    
    const failed = containerMetrics.filter(pod => 
      pod.phase === 'Failed' &&
      (pod.log_capture?.log_level)
    ).length;
    
    const pending = containerMetrics.filter(pod => 
      pod.phase === 'Pending' &&
      (pod.log_capture?.log_level)
    ).length;
    
    const running = containerMetrics.filter(pod => 
      pod.phase === 'Running').length;
    
    return { failed, pending, running };
  };

  // Get the icon for a specific status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'warning':
        return <Warning12Filled color="#FDB022" />;
      case 'degraded':
        return <Warning12Filled color="#FDB022" />;
      case 'outage':
        return <ErrorCircle12Filled color="#F04438" />;
      case 'empty_cell':
        return <div className="h-5 w-5 bg-slate-200 rounded-full"></div>
      default:
        return null;
    }
  };

  const getStatusIconForDay = (status?: string) => {
    switch (status) {
      case 'warning':
        return <Warning20Filled color="#FDB022" />;
      case 'degraded':
        return <Warning20Filled color="#FDB022" />;
      case 'outage':
        return <ErrorCircle20Filled color="#F04438" />;
      case 'maintenance':
        return <WrenchFilled color="#6172F3" className="w-5 h-5"/>;
      case 'empty_cell':
        return <div className="h-5 w-5 bg-slate-200 rounded-full"></div>
      case 'operational':
        return <CheckmarkCircle20Filled color="#00B25D" />;
      default:
        return null;
    }
  };

  return (
    <div className="box-border bg-white border border-[#E4E7EC] shadow-xl rounded-lg overflow-x-auto">
      <table className="w-full min-w-max">
        <thead>
          <tr className="text-gray-600 text-sm bg-gray-50">
            <th className="text-left py-2 px-4 w-48 font-inter font-semibold text-[#475467] border-b border-[#E4E7EC]">Services Names</th>
            <th className="text-left py-2 px-4 w-32 font-inter font-semibold text-[#475467] border-b border-[#E4E7EC]">Status</th>
            {days.map(day => (
              <th 
                key={day} 
                className={`text-center py-2 px-1 w-8 font-inter font-semibold text-[#475467] border-b border-[#E4E7EC] ${isTodayVisible && day === todayDate ? 'bg-gray-200' : ''}`}
              >
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metricsLoading && Object.keys(podsByContainer).length === 0 ? (
            <tr className="hover:bg-gray-50">
              <td colSpan={33} className="text-center py-8 border-r border-b border-[#E4E7EC]">
                <Loading />
              </td>
            </tr>
          ) : metricsError ? (
            <tr className="hover:bg-gray-50">
              <td colSpan={33} className="text-center py-8 text-red-500 border-r border-b border-[#E4E7EC]">
                {t('Error loading data:')} {metricsError}
              </td>
            </tr>
          ) : (
            // Original components section is commented out
            <></>
          )}
          
          {/* Pod Services integrated into main table */}
          {!metricsLoading && !metricsError && Object.keys(podsByContainer).length > 0 && 
            Object.entries(podsByContainer).map(([containerName, pods]) => {
              // Get container status based on its pods
              const hasFailedPods = pods.some(pod => pod.phase === 'Failed');
              const hasPendingPods = pods.some(pod => pod.phase === 'Pending');
              const hasMaintenancePods = pods.some(pod => pod.phase === 'Maintenance');
              const hasOutageRunningPods = pods.some(pod => pod.phase === 'Running');
              
              // Check if this service is in active maintenance based on pod metrics
              // This keeps the maintenance text in the status column
              let status = 'Unknown';
              if (hasMaintenancePods) {
                status = 'Maintenance';
              } else if (hasFailedPods) {
                status = 'Outage';
              } else if (hasPendingPods) {
                status = 'Degraded';
              } else if (hasOutageRunningPods) {
                status = 'Operational';
              }
              const containerLag = lagByClientId[containerName] || 0;
              
              // Get the original container name for historical data lookup
              const originalContainerName = pods[0]?.container_name || containerName;
              
              // We'll use the critical services from the component level
              const serviceName = originalContainerName || containerName;
              const isCritical = criticalServicesMap[serviceName] || false;
              
              return (
                <tr key={`container-${containerName}`} className="border-t border-gray-200 bg-white hover:bg-gray-50">
                  <td className="py-3 px-4 border-r border-b border-[#E4E7EC]">
                    <div className="flex items-center">
                      {isCritical ? (
                        <div className="mr-5">
                          <Flag16Filled color="#e81818" />
                        </div>
                      ) : (
                        <div className="mr-5">
                          <Flag16Regular />
                        </div>
                      )}
                      <div className="font-inter font-normal text-base text-[#344054]">
                        {containerName}
                        {containerLag > 0 && (
                          <span className={`ml-2 text-xs ${containerLag > 1000 ? 'text-red-500' : 'text-yellow-500'}`}>
                            (Lag: {containerLag})
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  {/* Status column for container */}
                  <td className="py-3 px-4 border-r border-b border-[#E4E7EC]">
                    <div className="flex justify-center">
                      <span className={`font-inter font-normal text-base ${statusTextColorMap[status as StatusType] || 'text-green-500'}`}>
                        {status}
                      </span>
                    </div>
                  </td>
                  
                  {/* Status cells for the container row */}
                  {days.map(day => {
                    // Use original container name for historical data lookup
                    const dayStatusObj = getHistoricalStatusForDay(originalContainerName, day);
                    const dayStatus = dayStatusObj.status;
                    const statusCounts = getDayStatusCounts(originalContainerName, day);
                    return (
                      <td 
                        key={`container-${containerName}-${day}`} 
                        className={`text-center py-1 px-1 border-r border-b border-[#E4E7EC] 
                          ${isTodayVisible && day === todayDate ? 'bg-gray-200' : 
                            isCurrentDay(day) && dayStatus && dayStatus !== 'empty_cell' ? getStatusColorForDay(dayStatus) : ''}`}
                      >
                        {isCurrentDay(day) ? (
                          <div className="flex items-center justify-center">
                            {dayStatus === 'warning' || dayStatus === 'outage' ? (
                              <Tooltip
                                content={
                                  <div className="flex flex-col text-white rounded-md p-3 shadow-lg relative max-w-[300px]">
                                    <div className="flex items-center justify-between w-full mb-2">
                                      <div className="flex items-center gap-1">
                                        <div className="flex items-center">
                                          {getStatusIcon(dayStatus === 'outage' ? 'outage' : 'warning')}
                                        </div>
                                        <span className={`font-bold text-xs ${dayStatus === 'outage' ? 'text-red-500' : 'text-yellow-500'}`}>
                                          {dayStatus === 'outage' ? 'Error' : 'Warning'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-300 mb-2">
                                      {dayStatus === 'outage' ? 'Service experienced an outage on this day' : 'Service was degraded on this day'}
                                    </div>
                                    <div className="text-xs text-gray-300">
                                      {statusCounts.failed > 0 && (
                                        <div className="flex justify-between mb-1">
                                          <span className="text-red-500">Failed: {statusCounts.failed}</span>
                                        </div>
                                      )}
                                      {statusCounts.pending > 0 && (
                                        <div className="flex justify-between mb-1">
                                          <span className="text-yellow-500">Warning: {statusCounts.pending}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                }
                                placement="top"
                                className="z-50"
                                animate={{
                                  mount: { scale: 1, y: -10 },
                                  unmount: { scale: 0, y: 0 },
                                }}
                                placeholder={undefined}
                                onResize={undefined}
                                onResizeCapture={undefined}
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                              >
                                <div 
                                  className="cursor-pointer" 
                                  onClick={() => handleHistoricalMetricsClick(originalContainerName, undefined, day)}
                                >
                                  {getStatusIconForDay(dayStatus || 'empty_cell')}
                                </div>
                              </Tooltip>
                            ) : dayStatus === 'maintenance' ? (
                              <Tooltip
                                content={
                                  <div className="flex flex-col text-white rounded-md p-3 shadow-lg relative max-w-[300px]">
                                    <div className="flex items-center justify-between w-full mb-2">
                                      <div className="flex items-center gap-1">
                                        <div className="flex items-center">
                                          <WrenchFilled color="#6172F3" className="w-4 h-4"/>
                                        </div>
                                        <span className="font-bold text-xs text-indigo-400">
                                          Maintenance
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-300 mb-2">
                                      {"Service was in maintenance mode on this day"}
                                    </div>
                                  </div>
                                }
                                placement="top"
                                className="z-50"
                                animate={{
                                  mount: { scale: 1, y: -10 },
                                  unmount: { scale: 0, y: 0 },
                                }}
                                placeholder={undefined}
                                onResize={undefined}
                                onResizeCapture={undefined}
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                              >
                                <div className="cursor-pointer">
                                  {getStatusIconForDay(dayStatus || 'empty_cell')}
                                </div>
                              </Tooltip>
                            ) : (
                              getStatusIconForDay(dayStatus || 'empty_cell')
                            )}
                          </div>
                        ) : isFutureDay(day) ? (
                          <div className="flex items-center justify-center">
                            {getStatusIconForDay('empty_cell')}
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          }
        </tbody>
      </table>
    </div>
  );
};

// Add missing imports
import { Warning12Filled, ErrorCircle12Filled, WrenchFilled, Warning20Filled, ErrorCircle20Filled, CheckmarkCircle20Filled } from '@fluentui/react-icons';

export default StatusHistoryTable; 